import {z} from 'zod';

import {RoleFirebaseStringSchema} from './role';

// ============================================================================
// Role Document Schema
// ============================================================================

/**
 * Zod schema for the canonical role document stored at
 * `users/{userId}/roles/{courseId}` in Firestore.
 *
 * This document is the **source of truth** for a user's role in a course.
 * The reconciliation job never writes this document — it only amends derived
 * state (roster docs, confirmed-status flags) to match what this document says.
 *
 * Shape: `{ role: RoleFirebaseString }`.
 */
export const roleDocumentSchema = z.object({
  /**
   * The user's canonical role for the course. Must be one of the 8 recognized
   * role strings defined in {@link RoleFirebaseStringSchema}.
   */
  role: RoleFirebaseStringSchema.describe(
    'The canonical role string for this user/course pair.',
  ),
});

/**
 * TypeScript type inferred from {@link roleDocumentSchema}.
 * Represents the data shape of `users/{userId}/roles/{courseId}`.
 */
export type RoleDocument = z.infer<typeof roleDocumentSchema>;

// ============================================================================
// Firestore Path Helpers
// ============================================================================

/** Root collection name for per-user role subcollections. */
export const USER_ROLES_COLLECTION = 'roles' as const;

/**
 * Returns the Firestore path for a single role document.
 *
 * Path: `users/{userId}/roles/{courseId}`
 *
 * @param params - Path parameters.
 * @param params.userId - The Firebase Auth UID of the user.
 * @param params.courseId - The course identifier.
 * @return The absolute Firestore document path string.
 * @throws Never.
 */
export function getUserRoleDocumentPath({
  userId,
  courseId,
}: {
  userId: string;
  courseId: string;
}): string {
  return `users/${userId}/${USER_ROLES_COLLECTION}/${courseId}`;
}
